import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Modal } from "@/components/Modal";
import { Heart, ArrowLeft, Utensils, Hand } from "lucide-react";
import { TIFFIN_FOODS } from "@/data/tiffinFoods";

// A steel dabba (tiffin) that pops its lid off to reveal the day's lunch.
const TiffinArt = ({ open }) => (
  <div className="relative mx-auto h-40 w-44 select-none">
    {/* lid */}
    <motion.div
      initial={false}
      animate={open ? { y: -34, rotate: -12 } : { y: 0, rotate: 0 }}
      transition={{ type: "spring", stiffness: 140, damping: 12 }}
      className="absolute left-1/2 top-6 z-10 h-10 w-40 -translate-x-1/2 rounded-[14px] border-[3px] border-[#7d8790] bg-gradient-to-b from-[#e8edf1] to-[#b8c2ca]"
    >
      <div className="absolute left-1/2 -top-3 h-3 w-16 -translate-x-1/2 rounded-t-full border-[3px] border-b-0 border-[#7d8790] bg-[#cdd6dd]" />
    </motion.div>
    {/* body */}
    <div className="absolute inset-x-1 bottom-1 top-12 rounded-[16px] border-[3px] border-[#7d8790] bg-gradient-to-b from-[#d7dee4] to-[#a7b2ba] shadow-inner">
      <div className="absolute inset-x-3 top-2 h-1 rounded bg-white/50" />
      <div className="absolute inset-x-4 bottom-2 flex justify-between">
        <div className="h-6 w-1.5 rounded bg-[#8b969e]" />
        <div className="h-6 w-1.5 rounded bg-[#8b969e]" />
      </div>
      {/* clasp handles */}
      <div className="absolute -left-2 top-3 h-8 w-2 rounded-full border-2 border-[#7d8790] bg-[#c3ccd3]" />
      <div className="absolute -right-2 top-3 h-8 w-2 rounded-full border-2 border-[#7d8790] bg-[#c3ccd3]" />
    </div>
  </div>
);

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } } };
const pop = {
  hidden: { opacity: 0, y: 28, scale: 0.6 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 18 } },
};

export const TiffinOpenModal = ({ open, onClose, unlocked, onAdd, itemId }) => {
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (open) { setOpened(false); setSelected(null); } }, [open]);

  const handleOpen = () => { setOpened(true); if (itemId && !unlocked) onAdd(itemId); };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} testId="tiffin-open-modal" maxWidth="max-w-2xl">
      <div className="p-5 sm:p-7">
        <AnimatePresence mode="wait">
          {!opened && (
            <motion.div key="closed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center text-center">
              <h2 className="font-signboard text-3xl font-bold text-kirana-brown">Lunch Box / Tiffin</h2>
              <p className="mt-1 max-w-sm text-sm italic text-kirana-brown/70">
                "Lunch break ka countdown 3rd period se shuru."
              </p>
              <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} className="mt-4">
                <TiffinArt open={false} />
              </motion.div>
              <button data-testid="tiffin-open-btn" onClick={handleOpen}
                className="mt-5 flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-green px-6 py-2.5 font-bold text-white transition-transform hover:scale-[1.04] hard-shadow">
                <Hand className="h-5 w-5" /> Open the tiffin
              </button>
            </motion.div>
          )}

          {opened && !selected && (
            <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-signboard flex items-center gap-2 text-2xl font-bold text-kirana-brown">
                    <Utensils className="h-6 w-6" /> Aaj Ke Lunch Me…
                  </h2>
                  <p className="text-xs text-kirana-brown/70">Tap any food to remember it</p>
                </div>
                {unlocked && (
                  <span data-testid="tiffin-memory-unlocked" className="flex items-center gap-1.5 rounded-md border-2 border-kirana-red bg-kirana-red/10 px-3 py-1.5 text-sm font-bold text-kirana-red">
                    <Heart className="h-4 w-4 fill-kirana-red" /> Memory unlocked
                  </span>
                )}
              </div>

              <div className="relative mt-4">
                {/* open tiffin back-layer, so foods feel like they came out of it */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-52 -translate-x-1/2 -translate-y-1/2 rounded-[26px] border-4 border-dashed border-[#9aa5ad]/40 bg-[#e8edf1]/20" />
                <motion.div variants={stagger} initial="hidden" animate="show"
                  className="relative grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {TIFFIN_FOODS.map((f) => (
                    <motion.button key={f.id} variants={pop} data-testid={`tiffin-food-${f.id}`} onClick={() => setSelected(f)}
                      whileHover={{ scale: 1.06, rotate: -1 }} whileTap={{ scale: 0.96 }}
                      className="group flex flex-col overflow-hidden rounded-md border-[3px] border-kirana-brown text-left hard-shadow-sm"
                      style={{ background: f.color }}>
                      <div className="flex h-20 items-center justify-center bg-[#FDF6E3] p-1">
                        <img src={f.image} alt={f.name} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                      <div className="bg-card px-1.5 py-1">
                        <span className="line-clamp-1 text-[11px] font-bold text-kirana-brown">{f.name}</span>
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}

          {opened && selected && (
            <motion.div key="detail" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <button data-testid="tiffin-back-btn" onClick={() => setSelected(null)}
                className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5 text-sm font-bold text-kirana-brown hover:bg-kirana-gold">
                <ArrowLeft className="h-4 w-4" /> Back to tiffin
              </button>
              <div className="mt-3 flex h-52 items-center justify-center overflow-hidden rounded-md border-[3px] border-kirana-brown bg-[#FDF6E3]">
                <img data-testid="tiffin-detail-image" src={selected.image} alt={selected.name} className="h-full w-full object-cover" />
              </div>
              <h3 data-testid="tiffin-detail-name" className="mt-3 font-signboard text-3xl font-bold text-kirana-brown">{selected.name}</h3>
              <span className="mt-1 inline-block rounded border-2 border-kirana-brown bg-kirana-gold px-2 py-0.5 text-xs font-bold text-kirana-brown">{selected.tag}</span>
              <p className="mt-3 rounded-md border-2 border-dashed border-kirana-border bg-[repeating-linear-gradient(#FDF6E3,#FDF6E3_22px,#cdd4e6_23px)] p-3 text-sm italic text-kirana-brown">
                "{selected.memory}"
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  );
};
